/**
 * React Query configuration and hooks
 */

import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';

// Create a client with production-ready defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Query Provider component
export const QueryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

// Re-export hooks
export { useQuery, useMutation, useQueryClient };

// Query keys factory for type-safe query keys
export const queryKeys = {
  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
    activity: () => [...queryKeys.dashboard.all, 'activity'] as const,
  },

  // E-commerce
  ecommerce: {
    all: ['ecommerce'] as const,
    orders: (filters?: Record<string, unknown>) => [...queryKeys.ecommerce.all, 'orders', filters] as const,
    order: (id: string) => [...queryKeys.ecommerce.all, 'order', id] as const,
    estimate: (url: string) => [...queryKeys.ecommerce.all, 'estimate', url] as const,
    track: (code: string) => [...queryKeys.ecommerce.all, 'track', code] as const,
    payment: (code: string) => [...queryKeys.ecommerce.all, 'payment', code] as const,
    rate: () => [...queryKeys.ecommerce.all, 'rate'] as const,
    adminPayment: (code: string) => [...queryKeys.ecommerce.all, 'admin', 'payment', code] as const,
    adminQueue: () => [...queryKeys.ecommerce.all, 'admin', 'queue'] as const,
  },

  // Quotes (penawaran jastip)
  quotes: {
    all: ['quotes'] as const,
    mine: () => [...queryKeys.quotes.all, 'mine'] as const,
    byCode: (code: string) => [...queryKeys.quotes.all, code] as const,
    admin: (status?: string) => [...queryKeys.quotes.all, 'admin', status] as const,
    adminByCode: (code: string) => [...queryKeys.quotes.all, 'admin', 'detail', code] as const,
  },

  // Catalog (etalase toko + lisensi)
  catalog: {
    all: ['catalog'] as const,
    listings: (filters?: Record<string, unknown>) => [...queryKeys.catalog.all, 'listings', filters] as const,
    listing: (slug: string) => [...queryKeys.catalog.all, 'listing', slug] as const,
    myLicenses: (reveal?: boolean) => [...queryKeys.catalog.all, 'licenses', 'mine', reveal] as const,
    adminListings: () => [...queryKeys.catalog.all, 'admin', 'listings'] as const,
    adminLicenseStock: () => [...queryKeys.catalog.all, 'admin', 'licenses'] as const,
    adminRequests: (status?: string) =>
      [...queryKeys.catalog.all, 'admin', 'requests', status] as const,
  },

  // Software
  // Orders (alias for ecommerce for backward compatibility)
  orders: {
    all: ['ecommerce'] as const,
    list: (filters?: Record<string, unknown>) => ['ecommerce', 'orders', filters] as const,
    detail: (id: string) => ['ecommerce', 'order', id] as const,
  },

  // User
  user: {
    all: ['user'] as const,
    profile: ['user', 'profile'] as const,
    notifications: () => ['user', 'notifications'] as const,
  },
} as const;

export default queryClient;
